import type { ApiError, ApiResponse, PageMeta } from '@navicosa/shared-types';

/**
 * Gọi API /api/v1 với envelope PRD 9.
 * - Access token chỉ giữ trong bộ nhớ (không localStorage → giảm rủi ro XSS lấy token).
 * - Hết hạn (401) → tự gọi /admin/auth/refresh bằng cookie httpOnly rồi thử lại một lần.
 */
const BASE = '/api/v1';

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
let onSessionExpired: () => void = () => {};

export function setAccessToken(t: string | null) {
  accessToken = t;
}
export function setSessionExpiredHandler(fn: () => void) {
  onSessionExpired = fn;
}

export class ApiRequestError extends Error {
  constructor(
    public readonly status: number,
    public readonly error: ApiError,
  ) {
    super(error.message);
  }
}

export async function refreshAccessToken(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await fetch(`${BASE}/admin/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!res.ok) return false;
      const body = (await res.json()) as ApiResponse<{ access_token: string }>;
      setAccessToken(body.data?.access_token ?? null);
      return Boolean(body.data?.access_token);
    } catch {
      return false;
    } finally {
      setTimeout(() => (refreshing = null), 0);
    }
  })();
  return refreshing;
}

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

async function raw<T>(
  method: Method,
  path: string,
  body?: unknown,
  retry = true,
): Promise<ApiResponse<T>> {
  const headers: Record<string, string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (accessToken) headers.Authorization = `Bearer ${accessToken}`;
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers,
    credentials: 'include',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  let parsed: ApiResponse<T>;
  try {
    parsed = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiRequestError(res.status, {
      code: 'NETWORK_ERROR',
      message: 'Không kết nối được máy chủ',
      details: [],
    });
  }
  if (res.status === 401 && retry && !path.startsWith('/admin/auth/')) {
    if (await refreshAccessToken()) return raw<T>(method, path, body, false);
    onSessionExpired();
  }
  if (!parsed.success || !res.ok) {
    throw new ApiRequestError(
      res.status,
      parsed.error ?? { code: 'HTTP_ERROR', message: res.statusText, details: [] },
    );
  }
  return parsed;
}

/** Tải file (PDF…) cần đăng nhập – không đưa token lên URL. Hết hạn thì làm mới rồi thử lại. */
export async function fetchBlob(path: string, retry = true): Promise<Blob> {
  const res = await fetch(`${BASE}${path}`, {
    headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
    credentials: 'include',
  });
  if (res.status === 401 && retry && (await refreshAccessToken())) return fetchBlob(path, false);
  if (!res.ok)
    throw new ApiRequestError(res.status, {
      code: 'HTTP_ERROR',
      message: 'Không tải được tệp',
      details: [],
    });
  return res.blob();
}

export const api = {
  async get<T>(path: string, query?: Record<string, string | number | boolean | undefined | null>) {
    const qs = query
      ? '?' +
        Object.entries(query)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    return (await raw<T>('GET', path + (qs === '?' ? '' : qs))).data as T;
  },
  async page<T>(
    path: string,
    query?: Record<string, string | number | boolean | undefined | null>,
  ) {
    const qs = query
      ? '?' +
        Object.entries(query)
          .filter(([, v]) => v !== undefined && v !== null && v !== '')
          .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`)
          .join('&')
      : '';
    const r = await raw<T[]>('GET', path + (qs === '?' ? '' : qs));
    return { items: (r.data ?? []) as T[], meta: r.meta as PageMeta };
  },
  post: async <T>(path: string, body?: unknown) =>
    (await raw<T>('POST', path, body ?? {})).data as T,
  patch: async <T>(path: string, body: unknown) => (await raw<T>('PATCH', path, body)).data as T,
  put: async <T>(path: string, body: unknown) => (await raw<T>('PUT', path, body)).data as T,
  del: async <T>(path: string) => (await raw<T>('DELETE', path)).data as T,
};

/** Thông điệp lỗi hiển thị cho người dùng, kèm chi tiết nếu có. */
export function errorText(e: unknown): string {
  if (e instanceof ApiRequestError) {
    const details = (e.error.details as { message?: string; path?: string }[])
      .map((d) => d.message)
      .filter(Boolean);
    return details.length ? `${e.error.message}: ${details.join('; ')}` : e.error.message;
  }
  return e instanceof Error ? e.message : 'Đã có lỗi xảy ra';
}
